import type { ProductFeedbackType, UserRole } from '@doneverse/shared'
import { Bug, Lightbulb, MessageCircle, type LucideIcon } from 'lucide-react'

/**
 * Copy for the three submission forms, verbatim from the Feedback Loop brief
 * (§4.1). One form per type, because a single generic box is exactly why the
 * feedback we get today is vague and unactionable — each type has to ask its own
 * questions. The structured fields are also what the digest later clusters on.
 */

export interface FeedbackFormCopy {
  type: ProductFeedbackType
  /** Picker card. */
  pickerTitle: string
  pickerPrompt: string
  icon: LucideIcon
  /** Form. */
  formTitle: string
  formSubtitle: string
  labelQuestion: string
  labelPlaceholder: string
  descriptionQuestion: string
  descriptionPlaceholder: string
  /** Absent for general feedback, which captures no severity/impact. */
  impactQuestion?: string
}

/**
 * Order matters: general feedback stays last, deliberately. It's the catch-all,
 * and presented first it becomes the default — people should try the two
 * structured forms before falling back to the unstructured one.
 */
export const FEEDBACK_FORMS: FeedbackFormCopy[] = [
  {
    type: 'bug_report',
    pickerTitle: 'Report a bug',
    pickerPrompt: 'Something looks broken? Tell us what went wrong.',
    icon: Bug,
    formTitle: 'Report a bug',
    formSubtitle: 'Tell us what went wrong. The more specific, the faster we can fix it.',
    labelQuestion: "What's broken?",
    labelPlaceholder: 'e.g. Habit map does not refresh after saving',
    descriptionQuestion: 'What did you expect, and what happened instead?',
    descriptionPlaceholder:
      'What were you doing? What did you expect to happen, and what happened instead? A link, screenshot or screen recording is ideal.',
    impactQuestion: 'How urgent is this?',
  },
  {
    type: 'feature_request',
    pickerTitle: 'Request a feature',
    pickerPrompt: 'Got an idea? Send it straight to the product team.',
    icon: Lightbulb,
    formTitle: 'Request a feature',
    formSubtitle: 'Got an idea to make the dashboard better?',
    labelQuestion: 'The idea, in one line',
    labelPlaceholder: "e.g. Let me duplicate last week's plan",
    descriptionQuestion: 'What should it do, and why?',
    descriptionPlaceholder: 'What problem does this solve for you? How do you imagine it working?',
    impactQuestion: 'How important is this for you?',
  },
  {
    type: 'general',
    pickerTitle: 'General feedback',
    pickerPrompt: "Something on your mind that isn't a bug or an idea?",
    icon: MessageCircle,
    formTitle: 'General feedback',
    formSubtitle: 'Anything else on your mind.',
    labelQuestion: 'In one line',
    labelPlaceholder: 'e.g. The onboarding felt rushed',
    descriptionQuestion: 'Tell us more',
    descriptionPlaceholder: 'Tell us more…',
  },
]

export function feedbackFormCopy(type: ProductFeedbackType): FeedbackFormCopy {
  return FEEDBACK_FORMS.find((form) => form.type === type) ?? FEEDBACK_FORMS[0]
}

/**
 * The subheading on every form clarifying that this is not a support channel.
 * Varies by role; admins get none — they know where product work goes.
 */
export function supportRedirectCopy(role: UserRole | undefined): string | null {
  if (role === 'doer') {
    return 'This goes to our product team — bugs, ideas, and anything about how the dashboard works. For help with your work or your placement, contact your DSL.'
  }
  if (role === 'admin') return null
  return 'This goes to our Product Development Team — bugs, ideas, and anything about how the dashboard works. For help with your Doer, join the live support room or email founders@outsourceddoers.com.'
}

/** The submission forms cap the one-liner at ~140 characters (API enforces it too). */
export const FEEDBACK_LABEL_MAX_LENGTH = 140

/**
 * Which form schema produced a row. Bumped from 1 (the old single-textarea
 * widget) to 2 for the three-form redesign, so the digest can tell structured
 * submissions apart from the legacy free-text ones.
 */
export const FEEDBACK_FORM_VERSION = 2
